// src/store/inbox.ts
import { defineStore } from "pinia";
import http from "@/api/http";

export type InboxStatus = "new" | "read" | "handled" | "archived" | "all";

export interface InboxItem {
  id: number;
  nom?: string | null;
  email?: string;
  sujet?: string | null;
  message?: string | null;
  status?: InboxStatus | string;
  created_at?: string;
  updated_at?: string;
  last_incoming_at?: string | null;
  last_outgoing_at?: string | null;
  labels?: string[];
  message_count?: number;
  langue?: string;
  /** facultatif si le back l’envoie */
  has_attachments?: boolean;
}

export interface TimelineMsg {
  id: number;
  direction: "in" | "out";
  subject?: string | null;
  body_text?: string | null;
  attachments?: any[];
  created_at: string;
  from?: { name?: string | null; email?: string | null };
}

export interface InboxThread {
  inbox: (InboxItem & { attachments?: any[] }) | null;
  replies: Array<{
    id: number;
    subject: string | null;
    body_text: string | null;
    attachments?: any[];
    status: "sent" | "queued" | "failed" | "received";
    last_error?: string | null;
    created_at: string;
  }>;
  timeline?: TimelineMsg[];
}

interface InboxState {
  q: string;
  status: InboxStatus;
  page: number;
  pageSize: number;

  loadingList: boolean;
  items: InboxItem[];
  total: number;

  current: InboxItem | null;
  thread: InboxThread | null;
  loadingThread: boolean;
}

function normalizeUiStatus(dbStatus?: string): InboxStatus {
  // miroir du mapping côté service (dbToUiStatus)
  switch ((dbStatus || "").toLowerCase()) {
    case "new":
    case "waiting":
      return "new";
    case "open":
      return "read";
    case "closed":
      return "handled";
    case "archived":
      return "archived";
    default:
      return "read";
  }
}

function parseHasAttachmentToken(q: string): boolean {
  return /\bhas:attachment\b/i.test(q || "");
}

export const useInbox = defineStore("inbox", {
  state: (): InboxState => ({
    q: "",
    status: "new",
    page: 1,
    pageSize: 20,

    loadingList: false,
    items: [],
    total: 0,

    current: null,
    thread: null,
    loadingThread: false,
  }),

  actions: {
    setQuery(q: string) {
      this.q = q;
    },
    setStatus(s: InboxState["status"]) {
      this.status = s;
      this.page = 1;
      this.fetchList();
    },
    setPage(p: number) {
      this.page = Math.max(1, p);
      this.fetchList();
    },

    /** map l’onglet UI -> param status API ; peut retourner une liste CSV */
    _mapStatusForApi(ui: InboxStatus): string | undefined {
      if (ui === "all") return undefined;
      if (ui === "archived") return "archived";
      if (ui === "handled") return "closed";
      if (ui === "read") return "open";
      if (ui === "new") return "new,waiting";
      return undefined;
    },

    /** petite aide pour détecter « has:attachment » dans q et nettoyer le q envoyé côté API */
    _cleanQueryForApi(q: string): { q: string; wantAttachments: boolean } {
      const want = parseHasAttachmentToken(q);
      const cleaned = (q || "").replace(/\bhas:attachment\b/gi, "").trim();
      return { q: cleaned, wantAttachments: want };
    },

    async _fetchOnce(params: any) {
      const { data } = await http.get(`contact/threads`, { params });
      return data;
    },

    async fetchList() {
      this.loadingList = true;

      try {
        const statusParam = this._mapStatusForApi(this.status);
        const { q: cleanedQ, wantAttachments } = this._cleanQueryForApi(this.q);

        const baseParams: Record<string, any> = {
          page: this.page,
          pageSize: this.pageSize,
          q: cleanedQ || undefined,
        };

        let merged: any = null;

        if (statusParam && statusParam.includes(",")) {
          try {
            const data = await this._fetchOnce({ ...baseParams, status: statusParam });
            merged = data;
          } catch {
            const parts = statusParam.split(",").map((s) => s.trim()).filter(Boolean);
            const datasets = [];
            for (const p of parts) {
              try {
                datasets.push(await this._fetchOnce({ ...baseParams, status: p }));
              } catch {/* ignore ce morceau si 404 */}
            }
            const items = datasets.flatMap((d: any) => d?.items || d?.threads || []);
            const unique = new Map<number, any>();
            for (const it of items) unique.set(Number(it.id), it);
            merged = {
              items: Array.from(unique.values()),
              total: (datasets[0]?.total ?? 0) + (datasets[1]?.total ?? 0),
              page: this.page,
              pageSize: this.pageSize,
            };
          }
        } else {
          const data = await this._fetchOnce(
            statusParam ? { ...baseParams, status: statusParam } : baseParams
          );
          merged = data;
        }

        const rawItems = Array.isArray(merged?.items)
          ? merged.items
          : Array.isArray(merged?.threads)
          ? merged.threads
          : Array.isArray(merged)
          ? merged
          : [];

        let items: InboxItem[] = rawItems.map((r: any) => ({
          id: r.id,
          sujet: r.subject ?? r.sujet ?? "(sans sujet)",
          nom: r.user_name ?? r.nom ?? "",
          email: r.user_email ?? r.email ?? "",
          message: r.first_message ?? r.message ?? "",
          status: normalizeUiStatus(r.status),
          created_at: r.created_at,
          updated_at: r.updated_at,
          last_incoming_at: r.last_incoming_at ?? null,
          last_outgoing_at: r.last_outgoing_at ?? null,
          message_count: r.message_count ?? 0,
          labels: Array.isArray(r.labels)
            ? r.labels
            : String(r.labels || "")
                .split(",")
                .map((x: string) => x.trim())
                .filter(Boolean),
          langue: r.langue,
          has_attachments:
            typeof r.has_attachments === "boolean"
              ? r.has_attachments
              : undefined,
        }));

        if (this.status !== "all") {
          items = items.filter((it) => {
            const s = (it.status || "read") as InboxStatus;
            if (this.status === "new") return s === "new";
            if (this.status === "read") return s === "read";
            if (this.status === "handled") return s === "handled";
            if (this.status === "archived") return s === "archived";
            return true;
          });
        }

        if (wantAttachments) {
          items = items.filter((it) => {
            if (typeof it.has_attachments === "boolean") return it.has_attachments;
            return true;
          });
        }

        const key = (it: any) =>
          new Date(
            it?.last_incoming_at ||
              it?.last_outgoing_at ||
              it?.updated_at ||
              it?.created_at ||
              0
          ).getTime();
        items.sort((a, b) => key(b) - key(a));

        this.items = items;
        this.total =
          merged?.total ??
          (typeof merged?.count === "number" ? merged.count : items.length);

        if (this.current && !this.items.some((i) => i.id === this.current!.id)) {
          this.current = null;
          this.thread = null;
        }
        if (!this.current && this.items.length > 0) {
          await this.openThread(this.items[0].id);
        }
      } catch (err) {
        console.error("[inbox] fetchList error", err);
        this.items = [];
        this.total = 0;
      } finally {
        this.loadingList = false;
      }
    },

    async openThread(id: number) {
      this.loadingThread = true;
      try {
        const { data } = await http.get(`contact/threads/${id}`);

        if (data?.inbox) {
          data.inbox.status = normalizeUiStatus(data.inbox.status);
        }
        if (Array.isArray(data?.replies)) {
          data.replies = data.replies.map((r: any) => ({
            ...r,
            status: r.status || (r.direction === "in" ? "received" : "sent"),
          }));
        }

        this.thread = data || null;
        this.current = data?.inbox || null;
      } catch (e) {
        console.error("[inbox] openThread error", e);
        this.thread = null;
        this.current = null;
      } finally {
        this.loadingThread = false;
      }
    },

    async markStatus(s: InboxItem["status"]) {
      if (!this.current) return;
      try {
        await http.patch(`contact/threads/${this.current.id}/status`, { status: s });
      } finally {
        await this.fetchList();
      }
    },

    async bulkSetStatus(ids: number[], s: InboxItem["status"]) {
      if (!Array.isArray(ids) || !ids.length) return;
      try {
        await http.post(`contact/threads/bulk/status`, { ids, status: s });
      } finally {
        await this.fetchList();
      }
    },

    async handleMessageIngested(payload: any) {
      const tid =
        Number(payload?.threadId) ||
        Number(payload?.thread_id) ||
        Number(payload?.id);

      const key = (it: any) =>
        new Date(
          it?.last_incoming_at ||
            it?.last_outgoing_at ||
            it?.updated_at ||
            it?.created_at ||
            0
        ).getTime();

      if (!Number.isFinite(tid)) {
        await this.fetchList();
        return;
      }

      const idx = this.items.findIndex((it: any) => it.id === tid);
      if (idx >= 0) {
        const it = { ...this.items[idx] };
        if (payload?.subject) it.sujet = payload.subject;
        if (payload?.preview) it.message = payload.preview;
        const atIso =
          (payload?.at && new Date(payload.at).toISOString()) ||
          new Date().toISOString();
        it.last_incoming_at = atIso;
        it.updated_at = atIso;

        const arr = [...this.items];
        arr.splice(idx, 1, it);
        arr.sort((a, b) => key(b) - key(a));
        this.items = arr;
      } else {
        await this.fetchList();
      }

      if (this.current?.id === tid) {
        await this.openThread(tid);
      }
    },
  },
});
